import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home, Building2, Ruler, Layers, Table2, Gauge, Calculator, Check, Plus, Trash2,
  Save, Loader2, Wallet, Sigma, Boxes, Users, ClipboardCheck, Database, Tags, RotateCcw,
  ChevronLeft, ChevronDown, FilePlus2, FileDown, Map, HelpCircle, Maximize2, Building, X, ExternalLink,
  Car, Store, BedDouble, CookingPot, Utensils, Sofa, Bath, MoveVertical, Trees, Pencil, DoorOpen, LayoutTemplate, Cpu,
  AlertTriangle, Info, Activity, ArrowUp, ArrowDown, TrendingUp, Lock,
} from "lucide-react";
import { api, humanizeAxiosError } from "../../lib/api";
import { runRecalc } from "../../lib/recalcJob";
import { usePlanFeatures } from "../../hooks/usePlanFeatures";
import { ProgramaComercial } from "../../components/software/ProgramaComercial";
import { useAuth } from "../../context/AuthContext";
import {
  VM_FACADES, VM_DISTRIBS, VM_RENDER_3D,
  exportPlano2D, exportRender3D, exportPlanos2D3DPDF,
} from "./vmExports";
import { PlantaTecnica, ElevacionTecnica, exportExpedientePDF, MODEL_ACCENT } from "./VmPlanoTecnico";
import VmDesignStage from "./VmDesignStage";

/* ── Paleta oficial VM ───────────────────────────────────────────── */
const PETROL = "#0c2b38";
const PETROL_2 = "#0f3546";
const PETROL_3 = "#0a2430";
const NEON = "#22c55e";
const NEON_SOFT = "rgba(34,197,94,0.14)";
const BLUE = "#2563eb";

const TIPOS_PISO = ["Porcelanato", "Cerámico", "Pulido", "Laminado", "Alfombra", "Microcemento", "-"];

const money = (n) => `S/ ${Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const n2 = (n) => Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DEFAULT_AMBIENTES = [
  ["Cochera", "1", "Porcelanato", 15, 15, 1], ["Tienda / Bodega", "1", "Porcelanato", 9, 12, 0],
  ["Habitación Principal", "2", "Porcelanato", 12, 14, 1], ["Habitación Secundaria", "2", "Cerámico", 9, 9, 3],
  ["Cocina", "1", "Cerámico", 6, 8, 1], ["Comedor", "1", "Cerámico", 8, 9, 1],
  ["Sala / Living", "1", "Porcelanato", 10, 12, 1], ["Baño o Ss.hh", "1", "Cerámico", 3, 4, 1],
  ["Escalera", "1-3", "Cerámico", 0, 4.25, 1], ["Pasadizo", "1", "Porcelanato", 0, 11, 1], ["Área Libre (Patio)", "-", "Pulido", 0, 30, 1],
].map(([nombre, piso, tipoPiso, areaMin, areaProp, cantidad], i) => ({ id: `amb-${i + 1}`, nombre, piso, tipoPiso, areaMin, areaProp, cantidad }));

// Opciones de piso y resolución de ícono contextual (Lucide) por tipo de ambiente.
const PISO_OPTIONS = ["1", "2", "3", "4", "5", "1-3", "1-5", "-"];
const AMB_ICON = [
  [/cochera|garaje|estacion/i, Car],
  [/tienda|bodega|dep[oó]sito|local|comercio/i, Store],
  [/habitaci[oó]n|dormitorio|cuarto|suite/i, BedDouble],
  [/cocina/i, CookingPot],
  [/comedor/i, Utensils],
  [/sala|living|estar/i, Sofa],
  [/ba[nñ]o|ss\.?hh|servicio higi/i, Bath],
  [/escalera|circulaci/i, MoveVertical],
  [/[aá]rea libre|patio|jard[ií]n|terraza|azotea/i, Trees],
];
const ambIcon = (nombre = "") => (AMB_ICON.find(([re]) => re.test(nombre)) || [null, DoorOpen])[1];

// Área Libre (Patio): 'Área propuesta' es un % y el 'Área total' = (propuesta*100)/Área del terreno.
const AREA_LIBRE_RE = /[aá]rea\s*libre|patio/i;
const isAreaLibre = (amb) => AREA_LIBRE_RE.test(amb?.nombre || "");
const ambArea = (amb, areaTerreno) => {
  if (isAreaLibre(amb)) return (areaTerreno * (Number(amb.areaProp) || 0)) / 100;
  return (Number(amb.areaProp) || 0) * (Number(amb.cantidad) || 0); // 0 si Cantidad es 0
};

const _mkAmb = (arr) => arr.map(([nombre, piso, tipoPiso, areaMin, areaProp, cantidad], i) => ({ id: `amb-${i + 1}`, nombre, piso, tipoPiso, areaMin, areaProp, cantidad }));
const TEMPLATES = {
  estandar: { label: "Multifamiliar estándar", ambientes: DEFAULT_AMBIENTES },
  duplex: { label: "Dúplex compacto", ambientes: _mkAmb([
    ["Cochera", "1", "Pulido", 15, 15, 1], ["Sala / Living", "1", "Porcelanato", 10, 12, 1], ["Comedor", "1", "Porcelanato", 8, 9, 1],
    ["Cocina", "1", "Cerámico", 6, 7.5, 1], ["Baño o Ss.hh", "1", "Cerámico", 3, 3, 1],
    ["Habitación Principal", "2", "Porcelanato", 12, 14, 1], ["Habitación Secundaria", "2", "Porcelanato", 9, 10, 2],
    ["Baño o Ss.hh", "2", "Cerámico", 3, 3.5, 1], ["Escalera", "1-3", "Cerámico", 0, 6.5, 1], ["Área Libre (Patio)", "-", "Pulido", 0, 25, 1],
  ]) },
  comercio: { label: "Comercio + vivienda", ambientes: _mkAmb([
    ["Cochera", "1", "Pulido", 15, 18, 1], ["Tienda / Local comercial", "1", "Porcelanato", 20, 24, 2], ["Depósito", "1", "Cerámico", 8, 10, 1],
    ["Sala / Living", "2", "Porcelanato", 12, 14, 1], ["Comedor", "2", "Porcelanato", 9, 10, 1], ["Cocina", "2", "Cerámico", 7, 8, 1],
    ["Habitación Principal", "3", "Porcelanato", 12, 14, 1], ["Habitación Secundaria", "3", "Porcelanato", 9, 10.5, 2],
    ["Baño o Ss.hh", "2", "Cerámico", 3, 3.5, 2], ["Escalera", "1-3", "Cerámico", 0, 8, 1], ["Área Libre (Patio)", "-", "Pulido", 0, 30, 1],
  ]) },
};


/* ── Menú lateral (un solo sidebar, 3 grupos técnicos) ───────────── */
// Proyecto Sandoval en el motor COREX (V2 · S/ 448,287.47) para el Núcleo de Ingeniería.
const COREX_V2_PROJECT_ID = "d57394ea-1667-418a-977f-6a05914019cd";
const COREX_V2_PROJECT_NAME = "01 SANTISTEBAN · VIVIENDA MULTIFAMILIAR (V2)";
const NAV_GROUPS = [
  { label: "Planificación", items: [
    { key: "inicio", label: "INICIO", icon: Home, corex: null },
  ]},
  { label: "Núcleo de Ingeniería", items: [
    { key: "corex-nucleo", label: "INGENIERÍA Y GESTIÓN DE COSTOS", icon: Cpu, route: "/software/corex", adminOnly: true, badge: "V2" },
    { key: "mi-presupuesto", label: "MI PRESUPUESTO Y METRADOS", icon: Calculator, clientOnly: true, badge: "PRIVADO" },
  ]},
  { label: "Bases de Ingeniería", items: [
    { key: "base-estructural", label: "BD ESTRUCTURAS", icon: Database, badge: "NUEVO", corex: null, route: "/software/estructural" },
    { key: "actualizacion-precios", label: "PRECIOS $", icon: Tags, badge: "NUEVO", corex: null, route: "/software/actualizacion-precios?from=inicio" },
  ]},
];

/* ── Dropdown de toolbar ─────────────────────────────────────────── */
const ToolbarButton = ({ icon: Icon, label, testid, onClick, items, variant }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const primary = variant === "primary";
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        data-testid={testid}
        onClick={() => (items ? setOpen((v) => !v) : onClick?.())}
        className={primary
          ? "flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-bold text-white transition-colors"
          : "flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors"}
        style={primary ? { background: "#1D4ED8", border: "1px solid #1D4ED8" } : undefined}
        onMouseEnter={primary ? (e) => { e.currentTarget.style.background = "#1741b8"; } : undefined}
        onMouseLeave={primary ? (e) => { e.currentTarget.style.background = "#1D4ED8"; } : undefined}
      >
        <Icon size={14} className={primary ? "text-white" : "text-slate-500"} />
        <span className="hidden sm:inline">{label}</span>
        {items && <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""} ${primary ? "text-white/90" : "text-slate-400"}`} />}
      </button>
      {items && open && (
        <div className="absolute right-0 mt-1.5 w-60 rounded-xl border border-slate-100 bg-white shadow-lifted p-1.5 z-50" data-testid={`${testid}-menu`}>
          {items.map((it, i) =>
            it.divider ? <div key={i} className="my-1 h-px bg-slate-100" /> : (
              <button
                key={it.testid}
                data-testid={it.testid}
                onClick={() => { setOpen(false); it.onClick(); }}
                className="w-full flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-left text-[12px] font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              >
                {it.icon && <it.icon size={14} className="text-slate-400 shrink-0 mt-0.5" />}
                <span className="flex-1">
                  <span className="block font-bold">{it.label}</span>
                  {it.sub && <span className="block text-[10px] font-normal text-slate-400 mt-0.5">{it.sub}</span>}
                </span>
                {it.tag && <span className="text-[9px] font-bold text-white rounded px-1 py-0.5 mt-0.5" style={{ background: it.tagColor || BLUE }}>{it.tag}</span>}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
};

/* ── Gauge circular (donut) naranja→verde ────────────────────────── */
const AreaGauge = ({ value, color }) => {
  const v = Math.max(0, Math.min(100, value || 0));
  const R = 46, C = 2 * Math.PI * R;
  const off = C * (1 - v / 100);
  return (
    <div className="relative shrink-0" style={{ width: 128, height: 128 }} data-testid="vm-gauge">
      <svg width="128" height="128" viewBox="0 0 128 128" className="-rotate-90">
        <circle cx="64" cy="64" r={R} fill="none" stroke="#eef2f5" strokeWidth="12" />
        <circle cx="64" cy="64" r={R} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .5s ease, stroke .3s" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <div className="text-2xl font-extrabold leading-none" style={{ color }}>{(value || 0).toFixed(1)}%</div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">Utilización</div>
        </div>
      </div>
    </div>
  );
};

/* ── Bloque contenedor (tarjeta numerada compacta) ───────────────── */
const BLOCK_VARIANTS = {
  blue:   { c: "#1D4ED8", soft: "#F4FAFF", head: "#E8F2FF", title: "#173a8a" },
  orange: { c: "#EA580C", soft: "#FFF7F1", head: "#FFEEE2", title: "#9a3412" },
  green:  { c: "#059669", soft: "#F1FCF7", head: "#E3F7EE", title: "#065f46" },
};
const Block = ({ n, title, icon: Icon, children, className = "", right = null, variant = "default", style }) => {
  const v = BLOCK_VARIANTS[variant];
  return (
  <section className={`shadow-lg rounded-xl flex flex-col overflow-hidden ${v ? "" : "bg-white border border-slate-200/70"} ${className}`}
    style={{ ...(v ? { background: v.soft, border: `1px solid ${v.c}`, borderLeft: `4px solid ${v.c}` } : {}), ...(style || {}) }}>
    <header className="flex items-center gap-2 px-3 py-1.5 border-b" style={v ? { borderColor: `${v.c}30`, background: v.head } : { borderColor: "#f1f5f9", background: "rgba(248,250,252,0.6)" }}>
      <span className="grid place-items-center h-5 w-5 rounded-md text-white text-[11px] font-extrabold shrink-0" style={{ background: v ? v.c : PETROL }}>{n}</span>
      <Icon size={14} className="shrink-0" style={{ color: v ? v.c : "#94a3b8" }} />
      <h3 className="text-[12px] font-bold tracking-tight flex-1 truncate uppercase" style={{ color: v ? v.title : "#1e293b" }}>{title}</h3>
      {right}
    </header>
    <div className="p-2.5 flex-1">{children}</div>
  </section>
  );
};

/* Lienzo Maestro · plano de planta 2D (SVG) que reacciona al Ancho/Largo del terreno.
   Estilo técnico: Gris Charcoal + líneas Celeste Metálicas. POC Fase de Visualización. */
const PlanoSVG = ({ ancho, largo }) => {
  const A = Math.max(0.5, Number(ancho) || 0), L = Math.max(0.5, Number(largo) || 0);
  const VW = 340, VH = 210, pad = 30;
  const scale = Math.min((VW - pad * 2) / A, (VH - pad * 2) / L);
  const w = A * scale, h = L * scale;
  const x = (VW - w) / 2, y = (VH - h) / 2;
  const gx = []; for (let m = 1; m < A; m++) gx.push(x + m * scale);
  const gy = []; for (let m = 1; m < L; m++) gy.push(y + m * scale);
  return (
    <div className="rounded-lg overflow-hidden border border-slate-700" style={{ background: "#263241" }} data-testid="vm-plano-svg">
      <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-slate-700">
        <span className="text-[10px] font-extrabold uppercase tracking-wide text-slate-300">Lienzo Maestro · Plano 2D</span>
        <span className="text-[9px] font-mono text-sky-300" data-testid="vm-plano-dims">{n2(A)} × {n2(L)} m · {n2(A * L)} m²</span>
      </div>
      <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full block" style={{ maxHeight: 220 }} role="img" aria-label="Plano de planta">
        <defs>
          <linearGradient id="planoStroke" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#7dd3fc" /><stop offset="55%" stopColor="#38bdf8" /><stop offset="100%" stopColor="#0284c7" />
          </linearGradient>
          <filter id="planoGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="0" stdDeviation="2.5" floodColor="#38bdf8" floodOpacity="0.55" />
          </filter>
        </defs>
        {gx.map((px, i) => <line key={`gx${i}`} x1={px} y1={y} x2={px} y2={y + h} stroke="#38bdf8" strokeOpacity="0.12" strokeWidth="0.6" />)}
        {gy.map((py, i) => <line key={`gy${i}`} x1={x} y1={py} x2={x + w} y2={py} stroke="#38bdf8" strokeOpacity="0.12" strokeWidth="0.6" />)}
        <rect x={x} y={y} width={w} height={h} fill="#38bdf8" fillOpacity="0.06" stroke="url(#planoStroke)" strokeWidth="2.4" filter="url(#planoGlow)" rx="2" />
        {/* Cota Ancho (X) */}
        <line x1={x} y1={y - 12} x2={x + w} y2={y - 12} stroke="#7dd3fc" strokeWidth="0.8" />
        <text x={x + w / 2} y={y - 15} fill="#bae6fd" fontSize="10" fontWeight="700" textAnchor="middle">{n2(A)} m</text>
        {/* Cota Largo (Y) */}
        <line x1={x - 12} y1={y} x2={x - 12} y2={y + h} stroke="#7dd3fc" strokeWidth="0.8" />
        <text x={x - 16} y={y + h / 2} fill="#bae6fd" fontSize="10" fontWeight="700" textAnchor="middle" transform={`rotate(-90 ${x - 16} ${y + h / 2})`}>{n2(L)} m</text>
        <text x={x + w / 2} y={y + h / 2 + 3} fill="#64748b" fontSize="9" fontWeight="700" textAnchor="middle" letterSpacing="1">TERRENO</text>
      </svg>
    </div>
  );
};

const NumField = ({ label, value, onChange, unit, testid }) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState("");
  const commit = () => { onChange(String(parseFloat(draft) || 0)); setFocused(false); };
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
      <div className="mt-1 flex items-center rounded-lg border border-slate-200 bg-slate-50 focus-within:border-slate-400 focus-within:bg-white transition-colors">
        <input type="text" inputMode="decimal" value={focused ? draft : n2(value)} data-testid={testid}
          onFocus={(e) => { setFocused(true); setDraft(String(value ?? 0)); setTimeout(() => e.target.select(), 0); }}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); e.target.blur(); } }}
          className="w-full bg-transparent px-2.5 py-1.5 text-sm font-bold text-slate-800 outline-none" />
        {unit && <span className="px-2 text-[11px] font-semibold text-slate-400">{unit}</span>}
      </div>
    </label>
  );
};


const SmartAmbInput = ({ value, disabled, onCommit, testid, id, title, className }) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState("");
  const commit = () => { onCommit(String(parseFloat(draft) || 0)); setFocused(false); };
  return (
    <input id={id} type="text" inputMode="decimal" disabled={disabled} data-testid={testid} title={title}
      value={focused ? draft : n2(value)}
      onFocus={(e) => { if (disabled) return; setFocused(true); setDraft(String(value ?? 0)); setTimeout(() => e.target.select(), 0); }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); e.target.blur(); } }}
      className={className} />
  );
};

const Stat = ({ label, value, accent = false, testid }) => (
  <div className="rounded-lg px-2.5 py-1.5 border" style={{ borderColor: accent ? NEON : "#e2e8f0", background: accent ? NEON_SOFT : "#f8fafc" }}>
    <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-base font-extrabold leading-tight" style={{ color: accent ? "#15803d" : "#0f172a" }} data-testid={testid}>{value}</div>
  </div>
);

/* ── Widget de Verificación de Control · Presupuesto COREX (Inicio · con desglose + alerta) ── */
const _pen = (n) => `S/ ${new Intl.NumberFormat("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0))}`;
const _num2 = (n) => new Intl.NumberFormat("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));
const Row = ({ l, v, bold, accent }) => (
  <div className={`flex items-center justify-between ${bold ? "font-extrabold" : ""}`} style={accent ? { color: accent } : {}}>
    <span className={bold ? "" : "text-slate-500"}>{l}</span><span className={bold ? "" : "text-slate-700 font-semibold"}>{_pen(v)}</span>
  </div>
);
const InicioCorexWidget = () => {
  const [t, setT] = React.useState(null);
  const [toggling, setToggling] = React.useState(false);
  const fetchTotal = React.useCallback(async () => {
    try { const { data } = await api.get("/vm/estructural/param-cd"); setT(data); } catch (e) { /* noop */ }
  }, []);
  const doToggleFlete = React.useCallback(async (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (toggling) return;
    const next = !(t?.fleteEnabled);
    setToggling(true);
    setT((prev) => (prev ? { ...prev, fleteEnabled: next } : prev)); // optimistic
    try { await api.post("/vm/estructural/flete-toggle", { enabled: next }); window.dispatchEvent(new Event("corex-refresh")); await fetchTotal(); }
    catch (err) { await fetchTotal(); } finally { setToggling(false); }
  }, [toggling, t, fetchTotal]);
  React.useEffect(() => { fetchTotal(); const id = setInterval(fetchTotal, 12000); window.addEventListener("corex-refresh", fetchTotal); return () => { clearInterval(id); window.removeEventListener("corex-refresh", fetchTotal); }; }, [fetchTotal]);
  const cdPuro = t?.disponible ? t?.costoDirecto : null;
  const total = t?.disponible ? (t?.costoDirectoConAjuste ?? t?.costoDirecto) : null;
  const has = total != null;
  const control = 304238.61;   // Base de referencia del desvío (CD golden Inicio) · muestra 0.00% en diseño base
  const dev = has ? (total - control) / control : 0;
  const modo = t?.modo || "ACUMULADA";
  const pisos = t?.pisos || 1;
  const isBase = (modo !== "ESPECIFICO") && pisos === 1;
  const critical = has && isBase && dev > 0.05; // ROJO sólo por EXCESO > 5% en el escenario base
  const st = !has ? "empty" : (isBase && (t?.enControl || Math.abs(dev) < 0.0001)) ? "control" : critical ? "critical" : "modified";
  const M = { empty: { c: "#94a3b8", bg: "#f1f5f9", bd: "#e2e8f0", lbl: "" }, control: { c: "#16a34a", bg: "#ecfdf5", bd: "#a7f3d0", lbl: "· diseño base" }, modified: { c: "#b45309", bg: "#fffbeb", bd: "#fde68a", lbl: "· modificado" }, critical: { c: "#dc2626", bg: "#fef2f2", bd: "#fecaca", lbl: "· ¡desvío crítico!" } }[st];
  const chapters = t?.chapters || [];
  const niveles = t?.niveles || [];
  const ajuste = t?.ajusteGlobal || 0;
  const pie = t?.pie || null;
  const fleteOn = !!t?.fleteEnabled;
  const fleteTot = Number(t?.fleteTotal || 0);
  const costoM2 = t?.costoM2Techado;
  const areaTech = t?.areaTechadaAcumulada;
  const modoLbl = modo === "ESPECIFICO" ? `NIVEL ${pisos}` : (pisos > 1 ? "ACUMULADO" : "1 PISO");
  return (
    <div className="relative group" data-testid="inicio-corex-widget" data-state={st}>
      <div className="flex items-center gap-2 px-2.5 h-9 rounded-lg border bg-white cursor-help transition-colors" style={{ borderColor: "#dbe4f0" }}>
        {critical ? <AlertTriangle size={13} style={{ color: "#dc2626" }} /> : <Wallet size={13} style={{ color: "#173a8a" }} />}
        <div className="leading-tight">
          <div className="text-[8px] font-bold uppercase tracking-wide flex items-center gap-1" style={{ color: "#64748b" }}>
            Costo Directo COREX
            <span className="grid place-items-center h-2 w-2 rounded-full ring-1 ring-white" style={{ background: M.c }} title={M.lbl || "sin datos"} data-testid="inicio-corex-estado" />
            <span className="rounded-sm px-1 py-[1px] text-[7px] font-extrabold" style={{ background: "#eef3fb", color: "#173a8a" }} data-testid="inicio-corex-modo">{modoLbl}</span>
          </div>
          <div className="text-[13px] font-black leading-none" style={{ color: "#0f2f66", fontVariantNumeric: "tabular-nums" }} data-testid="inicio-corex-total">{has ? _pen(total) : "S/ —"}</div>
        </div>
        {has && (st === "control" ? <Check size={13} style={{ color: "#16a34a" }} /> : critical ? <AlertTriangle size={13} style={{ color: "#dc2626" }} /> : <Info size={13} style={{ color: "#b45309" }} />)}
      </div>
      {has && (
        <div className="absolute right-0 top-full mt-1 z-50 w-72 rounded-xl border border-slate-200 bg-white shadow-2xl p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity text-[11px]" data-testid="inicio-corex-breakdown">
          {niveles.length > 1 && (
            <div className="mb-2" data-testid="inicio-corex-niveles">
              <div className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500 mb-1">Costo Directo por nivel ({modoLbl})</div>
              <div className="space-y-1">
                {niveles.map((nv) => (
                  <Row key={nv.nivel} l={`Nivel ${nv.nivel}${nv.incluyeCimentacion ? " · cimentación" : ""}${nv.factorAltura > 1 ? ` · +${Math.round((nv.factorAltura - 1) * 100)}%` : ""} (${_num2(nv.altura)} m)`} v={nv.subtotalConAjuste ?? nv.subtotal} />
                ))}
              </div>
              <div className="border-t border-slate-200 my-1.5" />
            </div>
          )}
          <div className="text-[10px] font-extrabold uppercase tracking-wide text-slate-500 mb-2">Costo Directo por capítulo</div>
          <div className="space-y-1">
            {chapters.map((c) => (
              <Row key={c.num} l={c.num === 2 && ajuste > 0 ? `${c.nombre} · incl. sobrecosto` : c.nombre} v={c.subtotal} />
            ))}
            <div className="border-t border-slate-200 my-1" />
            <Row l="TOTAL COSTO DIRECTO" v={total} bold accent={M.c} />
          </div>
          <div className="mt-2 flex items-center justify-between rounded-lg border px-2 py-1.5" style={{ borderColor: fleteOn ? "#93c5fd" : "#e2e8f0", background: fleteOn ? "#eff6ff" : "#f8fafc" }} data-testid="inicio-flete-toggle-box">
            <div className="leading-tight">
              <div className="text-[9px] font-bold uppercase tracking-wide" style={{ color: "#334155" }}>Capítulo 08 · Flete</div>
              <div className="text-[8px]" style={{ color: "#64748b" }}>{fleteOn ? `Incluido en CD · ${_pen(fleteTot)}` : "Excluido · S/ 0.00"}</div>
            </div>
            <button type="button" onClick={doToggleFlete} disabled={toggling} data-testid="inicio-flete-toggle" aria-busy={toggling}
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-wide transition-colors disabled:opacity-60"
              style={{ background: fleteOn ? "#2563eb" : "#e2e8f0", color: fleteOn ? "#fff" : "#475569" }}
              title="Sincroniza el interruptor de Flete con el Módulo de Fletes y el Pie de Presupuesto">
              {toggling ? <Loader2 size={10} className="animate-spin" /> : null}{toggling ? "…" : (fleteOn ? "ON" : "OFF")}
            </button>
          </div>
          {pie && (
            <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2 space-y-1" data-testid="inicio-corex-pie">
              <div className="text-[9px] font-extrabold uppercase tracking-wide text-slate-500 mb-0.5">Pie de presupuesto</div>
              <Row l={`Gastos Generales`} v={pie.generalExpenses} />
              <Row l="Utilidad (10%)" v={pie.utility} />
              <Row l="IGV (18%)" v={pie.igv} />
              <Row l="Supervisión" v={pie.supervision} />
              <div className="border-t border-slate-200 my-0.5" />
              <Row l="PRESUPUESTO TOTAL" v={pie.total} bold accent={PETROL} />
            </div>
          )}
          {costoM2 != null && (
            <div className="mt-2 flex items-center justify-between rounded-lg px-2.5 py-1.5" style={{ background: "#0f172a" }} data-testid="inicio-corex-m2">
              <div className="leading-tight">
                <div className="text-[8px] font-bold uppercase tracking-wide text-slate-400">Costo por m² techado</div>
                <div className="text-[7px] text-slate-500">{_num2(areaTech)} m² techados · incl. IGV</div>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="grid place-items-center h-3.5 w-3.5 rounded-full" style={{ background: costoM2 < 2500 ? "#22c55e" : "#f59e0b" }} data-testid="inicio-corex-m2-sello" title={costoM2 < 2500 ? "Competitivo (< S/ 2,500/m²)" : "Sobre mercado (> S/ 2,500/m²)"} />
                <span className="text-[13px] font-extrabold" style={{ color: costoM2 < 2500 ? "#4ade80" : "#fbbf24" }} data-testid="inicio-corex-m2-valor">{_pen(costoM2)}</span>
              </div>
            </div>
          )}
          <div className="mt-2 flex items-center justify-between text-[10px]"><span className="text-slate-400">Desvío CD vs base (S/ {_num2(control)})</span><span className="font-extrabold" style={{ color: M.c }}>{dev >= 0 ? "+" : ""}{_num2(dev * 100)}%</span></div>
          {critical && <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-red-50 border border-red-200 px-2 py-1.5 text-[10px] font-semibold text-red-700" data-testid="inicio-corex-alert"><AlertTriangle size={13} className="shrink-0 mt-0.5" /> Sobrecosto: supera la base Santisteban en más de 5%. Revise la viabilidad económica.</div>}
        </div>
      )}
    </div>
  );
};

// ── Análisis de Rentabilidad + Calculadora de Ventas Escalonada ──
const RentabilidadCard = ({ margen, incremento, onSet }) => {
  const [c, setC] = React.useState(null);
  const fetchC = React.useCallback(async () => {
    try { const { data } = await api.get("/vm/estructural/param-comparativo"); setC(data); } catch (e) { /* noop */ }
  }, []);
  React.useEffect(() => { fetchC(); const id = setInterval(fetchC, 15000); window.addEventListener("corex-refresh", fetchC); return () => { clearInterval(id); window.removeEventListener("corex-refresh", fetchC); }; }, [fetchC]);
  if (!c || !c.disponible) return null;
  const esc = c.escenarios || [];
  const umbral = c.umbralMercado || 2500;
  const mg = Number(margen ?? 20);
  const inc = Number(incremento ?? 2);
  const eff = esc.find((e) => e.masEficiente) || esc[esc.length - 1];
  const nPisos = eff ? eff.pisos : 5;
  const baseVentaM2 = eff && eff.costoM2 != null ? eff.costoM2 * (1 + mg / 100) : 0;
  return (
    <>
      <CollapsePanel title="Análisis de Rentabilidad por Altura" icon={TrendingUp} testid="vm-rentabilidad">
        <div className="p-2">
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-[auto_1fr_1fr_1fr] gap-x-2 bg-slate-800 px-2 py-1 text-[8px] font-bold uppercase text-white">
              <span>Pisos</span><span className="text-right">Presup. Total</span><span className="text-right">Á. Techada</span><span className="text-right">Costo/m²</span>
            </div>
            {esc.map((e) => (
              <div key={e.pisos} className={`grid grid-cols-[auto_1fr_1fr_1fr] gap-x-2 px-2 py-1 border-t border-slate-100 text-[10px] ${e.masEficiente ? "bg-emerald-50" : ""}`} data-testid={`vm-rent-row-${e.pisos}`}>
                <span className="font-extrabold text-slate-700 flex items-center gap-1">{e.pisos}{e.masEficiente && <span className="text-emerald-600" title="Escenario más eficiente">★</span>}</span>
                <span className="text-right font-semibold text-slate-700">{money(e.total)}</span>
                <span className="text-right text-slate-500">{n2(e.areaTechada)} m²</span>
                <span className="text-right font-extrabold flex items-center justify-end gap-1" style={{ color: e.costoM2 < umbral ? "#16a34a" : "#d97706" }} data-testid={`vm-rent-m2-${e.pisos}`}>
                  <span className="grid place-items-center h-2.5 w-2.5 rounded-full" style={{ background: e.costoM2 < umbral ? "#22c55e" : "#f59e0b" }} />{n2(e.costoM2)}
                </span>
              </div>
            ))}
          </div>
          <div className="text-[8px] text-slate-400 mt-1">Semáforo de mercado: verde &lt; S/ {n2(umbral)}/m² · ámbar &gt; S/ {n2(umbral)}/m². El escenario ★ amortiza mejor la cimentación.</div>
        </div>
      </CollapsePanel>

      <CollapsePanel title="Precio de Venta Escalonado" icon={Wallet} testid="vm-precio-escalonado">
        <div className="p-2">
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <div className="bg-slate-800 px-2 py-1 text-[9px] font-extrabold uppercase text-white flex items-center gap-1"><Wallet size={11} /> Edificio de {nPisos} pisos</div>
            <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 border-b border-slate-100">
              <label className="flex items-center gap-1 text-[10px] font-semibold text-slate-600">Margen utilidad
                <span className="flex items-center rounded border border-slate-200 bg-white px-1 focus-within:border-emerald-400">
                  <input data-testid="vm-margen-utilidad" type="number" min="0" step="1" value={margen ?? ""} onChange={(e) => onSet("margenUtilidad", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                    className="w-12 text-[10px] font-bold text-slate-700 text-right py-0.5 focus:outline-none bg-transparent" /><span className="text-[8px] font-bold text-slate-400">%</span>
                </span>
              </label>
              <label className="flex items-center gap-1 text-[10px] font-semibold text-slate-600">+/piso
                <span className="flex items-center rounded border border-slate-200 bg-white px-1 focus-within:border-emerald-400">
                  <input data-testid="vm-incremento-piso" type="number" min="0" step="0.5" value={incremento ?? ""} onChange={(e) => onSet("incrementoPisoVenta", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                    className="w-10 text-[10px] font-bold text-slate-700 text-right py-0.5 focus:outline-none bg-transparent" /><span className="text-[8px] font-bold text-slate-400">%</span>
                </span>
              </label>
            </div>
            {Array.from({ length: nPisos }, (_, i) => i + 1).map((k) => {
              const precio = baseVentaM2 * (1 + (inc / 100) * (k - 1));
              return (
                <div key={k} className="flex items-center justify-between px-2 py-1 border-t border-slate-50 text-[10px]" data-testid={`vm-venta-piso-${k}`}>
                  <span className="font-semibold text-slate-600">Piso {k} <span className="text-[8px] text-slate-400">{k > 1 ? `· +${n2(inc * (k - 1))}% altura` : "· base"}</span></span>
                  <span className="font-extrabold text-emerald-700" data-testid={`vm-venta-m2-${k}`}>{money(precio)} /m²</span>
                </div>
              );
            })}
          </div>
          <div className="text-[8px] text-slate-400 mt-1">Precio de venta base = Costo/m² × (1 + margen). Cada nivel superior suma el incremento comercial por altura (vista/plusvalía).</div>
        </div>
      </CollapsePanel>
    </>
  );
};


/* ── Resumen de Presupuesto (Inicio) · Espejo DINÁMICO del Excel Sandoval · Costo Directo reactivo ── */
// ── Panel colapsable minimalista (acordeón, cerrado por defecto) ──
const CollapsePanel = ({ title, icon: Icon, testid, defaultOpen = false, children }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="mt-2 rounded-lg border border-slate-200 overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`}
        className="w-full flex items-center gap-1.5 bg-slate-50 hover:bg-slate-100 px-2 py-1.5 text-[9px] font-extrabold uppercase tracking-wide text-slate-500 transition-colors">
        {Icon && <Icon size={11} />}<span className="flex-1 text-left">{title}</span>
        <ChevronDown size={13} className="transition-transform" style={{ transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open && <div data-testid={`${testid}-body`}>{children}</div>}
    </div>
  );
};

const SandovalResumenCard = () => {
  const [d, setD] = React.useState(null);
  const [updating, setUpdating] = React.useState(false);
  const [priceImpact, setPriceImpact] = React.useState(null);
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem("vm_last_price_impact");
      if (raw) { const o = JSON.parse(raw); if (o && Date.now() - (o.ts || 0) < 600000) setPriceImpact(o); }
    } catch (e) { /* noop */ }
    const onPrice = (e) => { if (e?.detail) setPriceImpact({ ...e.detail, ts: Date.now() }); };
    window.addEventListener("vm-precio-impact", onPrice);
    return () => window.removeEventListener("vm-precio-impact", onPrice);
  }, []);
  // ADR-COREX-001: el Resumen consume EXCLUSIVAMENTE COREX (/corex-chapters + /corex-total). El
  // frontend NO calcula; solo representa lo que devuelve el motor. Muestra "Actualizando…" mientras espera
  // la respuesta y no refresca los valores hasta recibirla.
  const fetchCh = React.useCallback(async () => {
    setUpdating(true);
    try {
      const [chRes, totRes] = await Promise.all([
        api.get("/vm/estructural/corex-chapters"),
        api.get("/vm/estructural/corex-total"),
      ]);
      const ch = chRes.data || {};
      const tot = totRes.data || {};
      const chapters = (ch.chapters || []).map((c) => ({ num: parseInt(c.code, 10) || c.code, nombre: c.name, subtotal: c.subtotal }));
      setD({
        disponible: true,
        chapters,
        costoDirecto: ch.directCost,
        costoDirectoConAjuste: ch.directCost,
        ajusteGlobal: 0,
        niveles: (ch.partida02 && ch.partida02.niveles ? ch.partida02.niveles.map((n) => ({ nivel: n.nivel, subtotal: n.monto, subtotalConAjuste: n.monto, factorAltura: n.factorAltura, altura: n.altura, incluyeCimentacion: n.incluyeCimentacion })) : []),
        acabados: [],
        modo: ch.modo || "ACUMULADA",
        pisos: ch.pisos || 1,
        enControl: tot.enControl,
        pie: {
          costoDirecto: tot.directCost, generalExpenses: tot.generalExpenses, utility: tot.utility,
          subtotal: tot.subtotal, igv: tot.igv, valorReferencial: tot.valorReferencial,
          supervision: tot.supervisionCost, total: tot.budgetTotal,
        },
      });
    } catch (e) { /* noop: conserva el último valor COREX recibido */ }
    finally { setUpdating(false); }
  }, []);
  React.useEffect(() => { fetchCh(); const id = setInterval(fetchCh, 12000); window.addEventListener("corex-refresh", fetchCh); return () => { clearInterval(id); window.removeEventListener("corex-refresh", fetchCh); }; }, [fetchCh]);
  if (!d) return (
    <div className="grid h-full min-h-[130px] place-items-center text-center" data-testid="vm-presupuesto-loading">
      <Loader2 size={22} className="animate-spin text-slate-300" />
    </div>
  );
  if (d.disponible === false) return (
    <div className="grid h-full min-h-[130px] place-items-center text-center text-[11px] text-slate-400 px-3" data-testid="vm-presupuesto-empty">
      El motor de presupuesto se está preparando. Guarda un cambio en Inicio para recalcular.
    </div>
  );
  const chapters = d.chapters || [];
  const enControl = d.enControl;
  const niveles = d.niveles || [];
  const acabados = d.acabados || [];
  const modo = d.modo || "ACUMULADA";
  const pisos = d.pisos || 1;
  const modoLbl = modo === "ESPECIFICO" ? `NIVEL ${pisos} (específico)` : (pisos > 1 ? `ACUMULADO · ${pisos} pisos` : "1 PISO + parapeto");
  const ajuste = d.ajusteGlobal || 0;
  const cdAjuste = d.costoDirectoConAjuste ?? d.costoDirecto;
  const pie = d.pie || null;
  return (
    <div data-testid="vm-presupuesto">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 text-[10px] font-bold" style={{ color: updating ? "#b45309" : (enControl ? "#16a34a" : "#173a8a") }} data-testid="vm-pres-status">
          {updating ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} {updating ? "Actualizando… (recalculando en COREX)" : `Presupuesto oficial COREX${enControl ? " · en control (diseño base)" : ""}`}
        </div>
        <span className="rounded-md px-1.5 py-[2px] text-[8px] font-extrabold text-white" style={{ background: PETROL }} data-testid="vm-pres-modo">COREX RUNTIME</span>
      </div>
      {/* Resumen ejecutivo: 7 capítulos + TOTAL (siempre visibles) */}
      <div className={`space-y-1 transition-opacity duration-300 ${updating ? "opacity-40" : "opacity-100"}`}>
        {chapters.map((c) => (
          <div key={c.num} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2 py-1" data-testid={`vm-esp-${String(c.num).padStart(2, "0")}`}>
            <span className="text-[10px] font-semibold text-slate-600 leading-tight">{c.nombre}{c.num === 2 && ajuste > 0 && <span className="text-[8px] font-normal text-slate-400"> · incl. sobrecosto por altura</span>}</span>
            <span className="text-[11px] font-extrabold font-mono text-slate-800 whitespace-nowrap" data-testid={`vm-esp-${String(c.num).padStart(2, "0")}-monto`}>{money(c.subtotal)}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-2.5 px-0.5" data-testid="vm-pres-total-btn">
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500" data-testid="vm-pres-total-label">Total Costo Directo</span>
        <span className={`font-mono text-xl font-black tracking-tight tabular-nums transition-opacity duration-300 ${updating ? "opacity-40 animate-pulse" : "opacity-100"}`} style={{ color: "#065f46" }} data-testid="vm-pres-total">{money(cdAjuste)}</span>
      </div>

      {priceImpact && (
        <div className="mt-2 flex items-start gap-1.5 rounded-lg border px-2.5 py-1.5"
          style={{ borderColor: priceImpact.delta >= 0 ? "#fde68a" : "#a7f3d0", background: priceImpact.delta >= 0 ? "#fffbeb" : "#ecfdf5" }}
          data-testid="vm-precio-impacto-banner">
          <Tags size={13} className="shrink-0 mt-0.5" style={{ color: priceImpact.delta >= 0 ? "#b45309" : "#16a34a" }} />
          <div className="leading-tight flex-1 min-w-0">
            <div className="text-[10px] font-extrabold uppercase tracking-wide" style={{ color: priceImpact.delta >= 0 ? "#b45309" : "#16a34a" }}>Actualización de Insumos</div>
            <div className="text-[10px] text-slate-500 truncate">{priceImpact.label}</div>
          </div>
          <button onClick={() => { setPriceImpact(null); try { localStorage.removeItem("vm_last_price_impact"); } catch (e) {} }} data-testid="vm-precio-impacto-dismiss" className="text-slate-300 hover:text-slate-500"><X size={12} /></button>
        </div>
      )}

      {/* ── Detalle auditable en acordeones (cerrados por defecto) ── */}
      {niveles.length > 1 && (
        <CollapsePanel title="Resumen por niveles" icon={Layers} testid="vm-niveles-resumen">
          {niveles.map((nv) => {
            const rate = (nv.factorAltura || 1) - 1;
            const barColor = rate <= 0 ? "#16a34a" : rate <= 0.03 ? "#84cc16" : rate <= 0.06 ? "#eab308" : "#d97706";
            const bg = rate <= 0 ? "rgba(22,163,74,0.05)" : `rgba(217,119,6,${0.07 + rate * 2.4})`;
            return (
              <div key={nv.nivel} className="flex items-center justify-between gap-2 px-2 py-1 border-t border-slate-100" style={{ background: bg, borderLeft: `3px solid ${barColor}` }} data-testid={`vm-nivel-row-${nv.nivel}`}>
                <span className="text-[10px] font-semibold text-slate-600 flex items-center gap-1">
                  {rate > 0 && <span style={{ color: barColor }} className="text-[9px] font-extrabold">▲</span>}
                  Nivel {nv.nivel} <span className="text-[8px] text-slate-400">· {n2(nv.altura)} m{nv.incluyeCimentacion ? " · cimentación" : ""}{rate > 0 ? ` · +${Math.round(rate * 100)}%` : ""}</span>
                </span>
                <span className="text-[11px] font-extrabold whitespace-nowrap" style={{ color: rate > 0 ? "#b45309" : "#1e293b" }} data-testid={`vm-nivel-monto-${nv.nivel}`}>{money(nv.subtotalConAjuste ?? nv.subtotal)}</span>
              </div>
            );
          })}
        </CollapsePanel>
      )}
      {pie && (
        <CollapsePanel title="Pie de presupuesto (oficial COREX)" icon={Wallet} testid="vm-pie-presupuesto">
          {[["Costo Directo + Ajuste", pie.costoDirecto], ["Gastos Generales", pie.generalExpenses], ["Utilidad (10%)", pie.utility], ["Subtotal", pie.subtotal], ["IGV (18%)", pie.igv], ["Valor Referencial", pie.valorReferencial], ["Supervisión", pie.supervision]].map(([l, v]) => (
            <div key={l} className="flex items-center justify-between gap-2 px-2 py-[3px] border-t border-slate-50 text-[10px]">
              <span className="text-slate-500">{l}</span>
              <span className="font-mono font-semibold text-slate-700">{money(v)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-t border-slate-200" style={{ background: NEON_SOFT }}>
            <span className="text-[11px] font-extrabold text-emerald-800">PRESUPUESTO TOTAL</span>
            <span className="text-[13px] font-extrabold text-emerald-700" data-testid="vm-presupuesto-total">{money(pie.total)}</span>
          </div>
        </CollapsePanel>
      )}
      {acabados.length > 0 && (
        <CollapsePanel title="Balance de acabados" icon={Boxes} testid="vm-balance-acabados">
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 px-2 py-1 text-[8px] font-bold uppercase text-slate-400 border-b border-slate-100">
            <span>Material</span><span className="text-right">Área (m²)</span><span className="text-right">Costo (S/)</span>
          </div>
          {acabados.map((a) => (
            <div key={a.tipo} className="grid grid-cols-[1fr_auto_auto] gap-x-3 px-2 py-1 border-t border-slate-50 text-[10px]" data-testid={`vm-acabado-${a.tipo.split(" ")[0].toLowerCase()}`}>
              <span className="font-semibold text-slate-600">{a.tipo}</span>
              <span className="text-right font-mono text-slate-700" data-testid={`vm-acabado-area-${a.tipo.split(" ")[0].toLowerCase()}`}>{n2(a.metrado_m2)}</span>
              <span className="text-right font-extrabold text-slate-800" data-testid={`vm-acabado-costo-${a.tipo.split(" ")[0].toLowerCase()}`}>{n2(a.costo_total)}</span>
            </div>
          ))}
        </CollapsePanel>
      )}
      {/* Panel de impacto retirado: el Resumen refleja el estado COREX vigente (P1 · V1.1.1). */}
      <div className="mt-1.5 text-[9px] text-slate-400">Fuente única: COREX Runtime (/corex-chapters + /corex-total). El frontend no calcula (ADR-COREX-001).</div>
    </div>
  );
};

/* ── Panel de Impacto en Vivo: variación por especialidad del último cambio ── */
const ImpactoPanel = ({ impact }) => {
  if (!impact) return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50/60 px-2.5 py-2" data-testid="vm-impacto-panel" data-state="idle">
      <div className="flex items-center gap-1.5 text-[10px] font-extrabold text-slate-500 uppercase tracking-wide"><Activity size={12} /> Impacto del último cambio</div>
      <div className="mt-1 text-[10px] text-slate-400">Modifica una dimensión o ambiente para ver aquí la variación por especialidad.</div>
    </div>
  );
  const { deltas, cdDelta } = impact;
  const up = cdDelta > 0;
  const accent = Math.abs(cdDelta) < 0.01 ? "#64748b" : up ? "#dc2626" : "#16a34a";
  return (
    <div className="mt-2 rounded-lg border px-2.5 py-2" style={{ borderColor: up ? "#fde68a" : "#a7f3d0", background: up ? "#fffbeb" : "#ecfdf5" }} data-testid="vm-impacto-panel" data-state="active">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wide" style={{ color: accent }}><Activity size={12} /> Impacto del último cambio</div>
        <span className="text-[11px] font-extrabold" style={{ color: accent }} data-testid="vm-impacto-cd">{cdDelta >= 0 ? "+" : "−"}{money(Math.abs(cdDelta)).replace("S/ ", "S/ ")}</span>
      </div>
      <div className="mt-1 space-y-0.5">
        {deltas.length === 0 && <div className="text-[10px] text-slate-400">Sin variación en las especialidades.</div>}
        {deltas.map((x) => {
          const u = x.delta > 0;
          return (
            <div key={x.num} className="flex items-center justify-between gap-2" data-testid={`vm-impacto-${String(x.num).padStart(2, "0")}`}>
              <span className="text-[10px] font-semibold text-slate-600 leading-tight truncate">{x.nombre.replace(/^\d+\s*/, "")}</span>
              <span className="text-[10px] font-extrabold whitespace-nowrap flex items-center gap-0.5" style={{ color: u ? "#dc2626" : "#16a34a" }}>
                {u ? <ArrowUp size={10} /> : <ArrowDown size={10} />}{x.delta >= 0 ? "+" : "−"}{money(Math.abs(x.delta)).replace("S/ ", "S/ ")}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function VmDesignCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calc, setCalc] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);
  const [pisoSel, setPisoSel] = useState(1);
  const [selRow, setSelRow] = useState(null); // fila activa de la tabla de ambientes (solo resaltado UI)
  const [collapsed, setCollapsed] = useState(false);
  const [toast, setToast] = useState("");
  const [restaurando, setRestaurando] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const firstLoad = useRef(true);
  const recalcBusyRef = useRef(false);   // bloquea ediciones mientras un recálculo COREX sigue vivo

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2200); };
  const { features, isBasico, plan: planActual, licencia: miLicencia } = usePlanFeatures();
  // Upsell: informa y lleva a Planes cuando la función requiere Pro/Premium.
  const upsell = (msg) => { flash(msg); setTimeout(() => navigate("/planes"), 900); };
  // Puente de Exportación Oficial: iframe COREX oculto que ejecuta el MISMO servicio de exportación del Núcleo.
  const corexExportFrameRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    const onDone = (ev) => {
      const d = ev?.data;
      if (!d || d.type !== "COREX_EXPORT_DONE") return;
      setExporting(false);
      if (d.ok) flash("Documento oficial generado (formato idéntico al Núcleo COREX).");
      else if (d.error === "no-project") flash("Preparando el motor COREX… inténtalo de nuevo en unos segundos.");
      else flash("No se pudo generar la exportación oficial. Intenta nuevamente.");
    };
    window.addEventListener("message", onDone);
    return () => window.removeEventListener("message", onDone);
  }, []);

  const openCorex = (module) => {
    if (!p?.corexProjectId) { flash("Pulsa CALCULAR PROYECTO para generar el proyecto en COREX."); return; }
    try { window.localStorage.setItem("cl_corex_origin", "multifamiliar"); window.localStorage.setItem("cl_corex_autoopen", JSON.stringify({ projectId: p.corexProjectId, projectName: p.nombre, module })); } catch (e) { /* noop */ }
    navigate("/software/corex");
  };
  // Deep-link Comité: el botón admin abre directamente el Taller de Costos (apu-lista) del V2.
  const openCorexNucleo = async () => {
    flash("Sincronizando Oráculo COREX con Inicio…");
    let pid = null;
    try { const { data } = await api.post("/vm/estructural/reconcile-corex"); pid = data?.projectId || null; }
    catch (e) { flash("No se pudo preparar tu Oráculo COREX. Intenta de nuevo en unos segundos."); return; }
    if (!pid) { flash("No se pudo preparar tu Oráculo COREX. Intenta de nuevo."); return; }
    try { window.localStorage.setItem("cl_corex_origin", "multifamiliar"); window.localStorage.setItem("cl_corex_autoopen", JSON.stringify({ projectId: pid, projectName: COREX_V2_PROJECT_NAME, module: "presupuesto-general", lock: false })); } catch (e) { /* noop */ }
    navigate("/software/corex");
  };
  // Aislamiento total (no-admin): abre/crea el Oráculo Sandoval PRIVADO del usuario (clon del maestro)
  // en modo kiosco (sin acceso a la lista de proyectos ni al maestro).
  const [oraculoLoading, setOraculoLoading] = useState(false);
  const openMiOraculo = async () => {
    try {
      setOraculoLoading(true);
      flash("Preparando tu Oráculo Santisteban privado…");
      const { data } = await api.post("/corex-workspace/mi-oraculo");
      if (!data?.projectId) { flash("No se pudo abrir tu presupuesto privado."); return; }
      try { window.localStorage.setItem("cl_corex_origin", "multifamiliar"); window.localStorage.setItem("cl_corex_autoopen", JSON.stringify({ projectId: data.projectId, projectName: data.projectName, module: "presupuesto-general", lock: true })); } catch (e) { /* noop */ }
      navigate("/software/corex");
    } catch (e) { flash(humanizeAxiosError(e)); }
    finally { setOraculoLoading(false); }
  };
  // P3 · Metrados (admin): abre en el Workspace el MISMO clon del motor (_engine_pid) al que escribe el
  // Puente Estructural, NO el corexProjectId de Inicio. Resolución NO mutante: /mi-oraculo reutiliza el
  // vínculo corex_user_workspace del usuario (= _engine_pid), sin re-sincronizar metrados desde Inicio.
  const openMetrados = async () => {
    flash("Abriendo Metrados del motor…");
    let pid = null;
    try { const { data } = await api.post("/corex-workspace/mi-oraculo"); pid = data?.projectId || null; }
    catch (e) { /* best-effort: abrimos el portal igualmente */ }
    try {
      window.localStorage.setItem("cl_corex_origin", "multifamiliar");
      if (pid) window.localStorage.setItem("cl_corex_autoopen", JSON.stringify({ projectId: pid, projectName: COREX_V2_PROJECT_NAME, module: "metrados", lock: false }));
    } catch (e) { /* noop */ }
    navigate("/software/corex");
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/vm/proyecto");
        const t = data.terreno || {};
        if (t.altura1 === undefined) {
          const legacy = Number(t.alturaPiso) || 2.70;
          data.terreno = { ancho: t.ancho ?? 6, largo: t.largo ?? 20, altura1: legacy, alturaTipica: legacy, alturaParapeto: legacy };
        }
        // Migración Pasadizo: asegurar que SIEMPRE exista en la lista (proyectos previos al ajuste no lo traían).
        if (Array.isArray(data.ambientes) && !data.ambientes.some((a) => /pasadizo/i.test(a?.nombre || ""))) {
          const pasadizo = { id: `amb-pasadizo-${Date.now()}`, nombre: "Pasadizo", piso: "1", tipoPiso: "Porcelanato", areaMin: 0, areaProp: 6, cantidad: 1 };
          const idxPatio = data.ambientes.findIndex((a) => /área libre|area libre|patio/i.test(a?.nombre || ""));
          if (idxPatio >= 0) data.ambientes.splice(idxPatio, 0, pasadizo);
          else data.ambientes.push(pasadizo);
        }
        // Blindaje P0: el Área Libre (Patio) siempre lleva acabado (default 'Pulido') → suma al Cap. 03.
        if (Array.isArray(data.ambientes)) {
          data.ambientes = data.ambientes.map((a) =>
            (isAreaLibre(a) && (!a.tipoPiso || a.tipoPiso === "-")) ? { ...a, tipoPiso: "Pulido" } : a);
        }
        setP(data);
      }
      catch (e) { setErr(humanizeAxiosError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const set = useCallback((path, value) => {
    if (recalcBusyRef.current) { flash("COREX está recalculando… espera a que termine."); return; }
    setP((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  }, []);

  const buildBody = (src) => ({
    nombre: src.nombre, terreno: src.terreno, niveles: src.niveles, diseno: src.diseno,
    firma: src.firma || { ingeniero: "Ing. Santisteban", cip: "" },
    ambientes: src.ambientes, costoM2Ref: src.costoM2Ref, utilMin: src.utilMin, utilMax: src.utilMax,
    margenUtilidad: src.margenUtilidad ?? 20, incrementoPisoVenta: src.incrementoPisoVenta ?? 2,
  });

  /* Autoguardado con debounce */
  useEffect(() => {
    if (!p) return;
    if (firstLoad.current) { firstLoad.current = false; return; }
    const t = setTimeout(async () => {
      try {
        setSaving(true); await api.put("/vm/proyecto", buildBody(p)); setSaved(true); setTimeout(() => setSaved(false), 1500);
        // Async P0: encola el recálculo (202 + jobId) y hace polling hasta estado terminal. Los controles
        // quedan bloqueados (recalcBusyRef) mientras el job siga vivo → nunca edición superpuesta/spinner.
        flash("Recalculando presupuesto…");
        recalcBusyRef.current = true;
        const res = await runRecalc({ onProgress: (s) => { if (s === "STILL_RUNNING") flash("COREX continúa procesando el proyecto…"); } });
        if (res.status === "COMPLETED") { window.dispatchEvent(new Event("corex-refresh")); flash("Presupuesto actualizado"); }
        else if (res.status === "TIMEOUT") setErr("No se pudo iniciar el recálculo (COREX ocupado). Reintenta en unos segundos.");
        else setErr("No se pudo recalcular el presupuesto: " + (res.error || "error desconocido"));
      }
      catch (e) { setErr(humanizeAxiosError(e)); }
      finally { setSaving(false); recalcBusyRef.current = false; }
    }, 900);
    return () => clearTimeout(t);
  }, [p]);

  const geo = useMemo(() => {
    if (!p) return { area: 0, perimetro: 0, alturaTotal: 0 };
    const a = Number(p.terreno.ancho) || 0, l = Number(p.terreno.largo) || 0;
    const h1 = Number(p.terreno.altura1) || 0, ht = Number(p.terreno.alturaTipica) || 0;
    const N = Number(p.niveles.pisos) || 1;
    // Altura total = 1° piso + (N-1) pisos típicos (elevación hasta el nivel seleccionado).
    const alturaTotal = h1 + ht * Math.max(0, N - 1);
    return { area: a * l, perimetro: 2 * (a + l), alturaTotal };
  }, [p]);

  // Niveles seleccionados según el modo (corazón del sistema paramétrico).
  const floors = useMemo(() => {
    const N = Number(p?.niveles?.pisos) || 1;
    return p?.niveles?.modo === "ESPECIFICO" ? [N] : Array.from({ length: N }, (_, i) => i + 1);
  }, [p?.niveles?.modo, p?.niveles?.pisos]);

  // El piso analizado en Control de Área debe pertenecer a los niveles seleccionados.
  useEffect(() => { if (p && !floors.includes(pisoSel)) setPisoSel(floors[0]); }, [floors]);

  const ambientesPiso = useMemo(() => {
    if (!p) return 0;
    return p.ambientes
      .filter((amb) => String(amb.piso) === String(pisoSel) && amb.tipoPiso && amb.tipoPiso !== "-")
      .reduce((s, amb) => s + (Number(amb.areaProp) || 0) * Math.max(1, Number(amb.cantidad) || 1), 0);
  }, [p, pisoSel]);

  const control = useMemo(() => {
    const disponible = geo.area || 0;
    // Área Programada = OCUPACIÓN TOTAL del lote = TOTAL ÁREA TECHADA (Bloque 4) + Área Libre (Patio). Área Disponible = Área del Terreno (Bloque 1).
    const techada = p ? p.ambientes.filter((a) => !isAreaLibre(a)).reduce((s, a) => s + ambArea(a, disponible), 0) : 0;
    const patio = p ? p.ambientes.filter((a) => isAreaLibre(a)).reduce((s, a) => s + ambArea(a, disponible), 0) : 0;
    const programada = techada + patio;
    const util = disponible > 0 ? (programada / disponible) * 100 : 0;
    const estado = util > 100 ? "EXCEDE" : util >= 95 ? "OK" : "BAJO";
    const compat = estado === "OK";
    const color = estado === "OK" ? "#16a34a" : estado === "EXCEDE" ? "#dc2626" : "#f97316";
    const mensaje = estado === "OK" ? "Distribución de ambientes aprobado"
      : estado === "EXCEDE" ? "No procede: corrección de Distribución de ambientes"
      : "corrección de Distribución de ambientes";
    // Meta de área: cuánto sobra (>100%) o falta (para llegar al 95%) respecto al rango ideal 95–100%.
    const ideal = { min: disponible * 0.95, max: disponible };
    const meta = estado === "EXCEDE" ? { tipo: "sobran", valor: programada - ideal.max }
      : estado === "BAJO" ? { tipo: "faltan", valor: ideal.min - programada }
      : { tipo: "ok", valor: 0 };
    return { disponible, programada, techada, patio, util, compat, estado, color, mensaje, meta };
  }, [geo.area, p]);

  const totalTechada = useMemo(() => {
    if (!p) return 0;
    return p.ambientes.filter((a) => !isAreaLibre(a) && a.tipoPiso && a.tipoPiso !== "-")
      .reduce((s, a) => s + (Number(a.areaProp) || 0) * Math.max(1, Number(a.cantidad) || 1), 0);
  }, [p]);

  // TOTAL ÁREA TECHADA: suma SOLO ambientes techados; EXCLUYE 'Área Libre (Patio)' (Comité P0).
  const totalTabla = useMemo(() => {
    if (!p) return 0;
    const areaTerreno = (Number(p.terreno.ancho) || 0) * (Number(p.terreno.largo) || 0);
    return p.ambientes.filter((a) => !isAreaLibre(a)).reduce((s, a) => s + ambArea(a, areaTerreno), 0);
  }, [p]);

  const calcular = async () => {
    try { setCalc(true); setErr(""); const { data } = await api.post("/vm/proyecto/calcular", buildBody(p)); setP(data); flash(data.corexError ? ("Presupuesto calculado. Aviso COREX: " + data.corexError) : "Proyecto volcado al motor COREX. Abre Metrados en el menú."); }
    catch (e) { setErr(humanizeAxiosError(e)); }
    finally { setCalc(false); }
  };

  const guardarAhora = async () => {
    try {
      setSaving(true); await api.put("/vm/proyecto", buildBody(p)); setSaved(true); setTimeout(() => setSaved(false), 1500);
      flash("Recalculando presupuesto…");
      recalcBusyRef.current = true;
      const res = await runRecalc({ onProgress: (s) => { if (s === "STILL_RUNNING") flash("COREX continúa procesando el proyecto…"); } });
      if (res.status === "COMPLETED") { window.dispatchEvent(new Event("corex-refresh")); flash("Proyecto guardado · presupuesto actualizado"); }
      else if (res.status === "TIMEOUT") setErr("No se pudo iniciar el recálculo (COREX ocupado). Reintenta en unos segundos.");
      else setErr("No se pudo recalcular el presupuesto: " + (res.error || "error desconocido"));
    }
    catch (e) { setErr(humanizeAxiosError(e)); }
    finally { setSaving(false); recalcBusyRef.current = false; }
  };

  const nuevo = () => {
    if (!window.confirm("¿Crear una configuración nueva? Se restablecerán los valores por defecto.")) return;
    setP((prev) => ({
      ...prev, nombre: "VIVIENDA MULTIFAMILIAR",
      terreno: { ancho: 7.5, largo: 20, altura1: 3.0, alturaTipica: 2.70, alturaParapeto: 0.90 },
      niveles: { modo: "ACUMULADA", pisos: 1, incluirParapeto: true, factores: { p3: 3, p4: 6, p5: 9 } },
      diseno: { modeloId: "A", distribucionId: "01", vista: "PLANO2D", usoPrimerPiso: "COCHERA" },
      firma: { ingeniero: "Ing. Santisteban", cip: "" },
      ambientes: structuredClone(DEFAULT_AMBIENTES), costoM2Ref: 1350, utilMin: 95, utilMax: 100, margenUtilidad: 20, incrementoPisoVenta: 2, presupuesto: null,
    }));
    flash("Configuración nueva creada");
  };

  // Nuevo/Restaurar OFICIAL: restaura Inicio + BD Estructuras al estado oficial del Excel (servidor) y recalcula.
  const restaurarOficial = async () => {
    if (!window.confirm("¿Restaurar todos los datos al estado oficial? Esta acción sobreescribirá sus ediciones.")) return;
    setRestaurando(true);
    try {
      await api.post("/vm/estructural/restore-oficial");   // el backend dispara el puente automáticamente
      const { data } = await api.get("/vm/proyecto"); setP(data);
      window.dispatchEvent(new Event("corex-refresh"));
      flash("Datos restaurados al estado oficial del Excel. Presupuesto recalculado.");
    } catch (e) { setErr(humanizeAxiosError(e)); flash("No se pudo restaurar al estado oficial."); }
    finally { setRestaurando(false); }
  };

  const addAmbiente = () => set("ambientes", [...p.ambientes, { id: `amb-${Date.now()}`, nombre: "Nuevo ambiente", piso: String(pisoSel), tipoPiso: "Cerámico", areaMin: 0, areaProp: 0, cantidad: 1 }]);
  const rmAmbiente = (id) => set("ambientes", p.ambientes.filter((a) => a.id !== id));
  const setAmb = (id, key, val) => set("ambientes", p.ambientes.map((a) => (a.id === id ? { ...a, [key]: val } : a)));
  const applyTemplate = (key) => {
    const tpl = TEMPLATES[key];
    if (!tpl) return;
    if (!window.confirm(`¿Cargar la plantilla "${tpl.label}"? Reemplazará los ambientes actuales.`)) return;
    set("ambientes", structuredClone(tpl.ambientes));
    flash(`Plantilla "${tpl.label}" cargada`);
  };
  const ambMinDisplay = (amb) => {
    if ((Number(amb.areaMin) || 0) > 0) return `${n2(amb.areaMin)}`;
    if (String(amb.tipoPiso) === "-") return "Según norma (30%)";
    return "–";
  };
  const focusArea = (id) => { const el = document.getElementById(`vm-area-input-${id}`); if (el) { el.focus(); el.select?.(); } };
  const limpiarTodo = () => { if (window.confirm("¿Limpiar todos los ambientes?")) set("ambientes", []); };
  const wrap = (fn) => async () => { try { await fn(); flash("Descarga generada"); } catch (e) { flash("No se pudo exportar"); } };

  if (loading) return (
    <div className="grid h-full place-items-center" style={{ background: PETROL }}>
      <Loader2 className="animate-spin text-white" size={30} />
    </div>
  );
  if (!p) return <div className="p-8 text-red-600">{err || "No se pudo cargar el proyecto."}</div>;

  const modelo = VM_FACADES[p.diseno.modeloId] || VM_FACADES.A;
  const distrib = VM_DISTRIBS[p.diseno.distribucionId] || VM_DISTRIBS["01"];
  const previewImg = p.diseno.vista === "MODELO3D" ? VM_RENDER_3D : distrib.img;
  const pres = p.presupuesto;

  // Exportación Oficial Unificada: reconcilia el Oráculo con el diseño actual y ejecuta EXACTAMENTE
  // el mismo servicio/plantilla del Núcleo (bundle COREX vía iframe) → archivo binariamente idéntico.
  const runOfficialExport = async (kind, sheet) => {
    if (exporting) { flash("Generando documento… espera un momento."); return; }
    setExporting(true);
    let pid = null;
    if (isAdmin) { try { const { data } = await api.post("/vm/estructural/reconcile-corex"); pid = data?.projectId || null; } catch (e) { /* best-effort */ } }
    const win = corexExportFrameRef.current?.contentWindow;
    if (!win) { setExporting(false); flash("Preparando el motor COREX… inténtalo de nuevo en unos segundos."); return; }
    try { win.postMessage({ type: "COREX_EXPORT", kind, sheet, projectId: pid }, "*"); }
    catch (e) { setExporting(false); flash("No se pudo iniciar la exportación oficial."); return; }
    setTimeout(() => setExporting((v) => (v ? false : v)), 30000);
  };

  const docItems = [
    { testid: "vm-exp-excel", label: "Exportar Excel", sub: "Hoja actual (Presupuesto)", icon: Table2, tag: "XLSX", tagColor: "#1D4ED8", onClick: () => runOfficialExport("sheet", "Presupuesto") },
    { testid: "vm-exp-todo", label: "Exportar Todo (.xlsx)", sub: "Libro con las 9 pestañas oficiales", icon: Table2, tag: "XLSX", tagColor: "#1D4ED8", onClick: () => runOfficialExport("all") },
    { testid: "vm-exp-pdf", label: "Expediente PDF", sub: "Documentos integrados oficiales", icon: FileDown, tag: "PDF", tagColor: "#1D4ED8", onClick: () => runOfficialExport("pdf") },
  ].map((it) => features.export_doc ? it : { ...it, tag: "PRO", tagColor: "#7c3aed", sub: "Disponible en Pro y Premium", onClick: () => upsell("La exportación documental está disponible en Pro y Premium.") });
  const planoItems = [
    { testid: "vm-plano-2d", label: "Plano 2D (Distribución)", icon: Map, tag: "JPG", tagColor: "#dc2626", onClick: wrap(() => exportPlano2D(p)) },
    { testid: "vm-plano-3d", label: "Modelo 3D / Render", icon: Building, tag: "JPG", tagColor: "#dc2626", onClick: wrap(() => exportRender3D(p)) },
    { divider: true },
    { testid: "vm-plano-2d3d", label: "Plano 2D + 3D (PDF)", icon: FileDown, tag: "PDF", tagColor: "#dc2626", onClick: wrap(() => exportPlanos2D3DPDF(p)) },
  ].map((it) => (it.divider || features.export_planos) ? it : { ...it, tag: "PRO", tagColor: "#7c3aed", onClick: () => upsell("La exportación de planos está disponible en Pro y Premium.") });

  return (
    <div className="h-full flex text-slate-800 overflow-hidden" style={{ background: "#eef2f5" }} data-testid="vm-design-center">
      {/* Motor de Exportación Oficial COREX (oculto): ejecuta el mismo servicio/plantilla del Núcleo */}
      <iframe ref={corexExportFrameRef} title="Motor de exportación COREX" src="/corex-workspace/index.html"
        data-testid="vm-corex-export-frame" aria-hidden="true" tabIndex={-1}
        style={{ position: "fixed", width: 1, height: 1, left: -9999, top: -9999, opacity: 0, pointerEvents: "none", border: 0 }} />

      {/* ── Sidebar único petróleo ── */}
      <aside className={`hidden md:flex ${collapsed ? "w-16" : "w-56"} shrink-0 flex-col text-white transition-all duration-200`} style={{ background: PETROL }}>
        <div className={`border-b border-white/10 flex items-center justify-center bg-white ${collapsed ? "px-1 py-2" : "px-2.5 py-3"}`}>
          <img src="/corex-sidebar-logo.webp" alt="COREX Multifamiliar" data-testid="vm-sidebar-logo" className="w-full h-auto object-contain" />
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          {NAV_GROUPS.map((grp) => {
            const items = grp.items.filter((it) => (!it.adminOnly || isAdmin) && (!it.clientOnly || !isAdmin));
            if (!items.length) return null;
            return (
            <div key={grp.label} className="mb-1">
              {!collapsed && <div className="px-3 pt-2 pb-1 text-[9px] font-bold uppercase tracking-[0.12em] text-white/35">{grp.label}</div>}
              {items.map((it) => {
                const active = it.key === "inicio";
                return (
                  <button key={it.key} data-testid={`vm-nav-${it.key}`}
                    onClick={() => { if (active) return; if (it.key === "base-estructural" && !features.bd_estructural) { upsell("La Base de Datos Estructural está disponible en Pro y Premium."); return; } if (it.key === "mi-presupuesto") { openMiOraculo(); return; } if (it.key === "corex-nucleo") { openCorexNucleo(); return; } if (it.route) navigate(it.route); else if (it.corex) openCorex(it.corex); else flash("Módulo en construcción · disponible próximamente."); }}
                    title={it.label}
                    className="group relative w-full flex items-center gap-2.5 px-3 py-2 text-[12.5px] font-semibold transition-colors"
                    style={active ? { background: PETROL_2, color: "#fff" } : { color: "rgba(255,255,255,0.72)" }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = PETROL_3; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}>
                    {active && <span className="absolute left-0 top-0 h-full w-1" style={{ background: NEON }} />}
                    <it.icon size={16} style={{ color: active ? NEON : "inherit" }} className="shrink-0" />
                    {!collapsed && <span className="flex-1 text-left truncate">{it.label}</span>}
                    {!collapsed && it.badge && <span className="text-[8px] font-extrabold text-white rounded px-1 py-0.5" style={{ background: BLUE }}>{it.badge}</span>}
                  </button>
                );
              })}
            </div>
            );
          })}
        </nav>

        {!collapsed && (
          <div className="mx-2 mb-2 rounded-lg bg-white/5 border border-white/10 px-3 py-2.5" data-testid="vm-proyecto-card">
            <div className="text-[8px] font-bold uppercase tracking-wider text-emerald-300/70">Proyecto actual</div>
            <div className="text-[12px] font-extrabold leading-tight mt-0.5 truncate">{p.nombre}</div>
            <div className="text-[10px] text-white/55 mt-0.5">{p.niveles.pisos} pisos{p.niveles.incluirParapeto ? " + parapeto" : ""}</div>
            <div className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold text-emerald-300">
              <Check size={11} /> {saving ? "Guardando…" : "Guardado"}
            </div>
          </div>
        )}
        <button onClick={() => setCollapsed((v) => !v)} data-testid="vm-collapse-menu"
          className="flex items-center justify-center gap-1.5 border-t border-white/10 px-3 py-2.5 text-[11px] font-semibold text-white/60 hover:bg-white/5 transition-colors">
          <ChevronLeft size={14} className={`transition-transform ${collapsed ? "rotate-180" : ""}`} />
          {!collapsed && "Contraer menú"}
        </button>
      </aside>

      {/* ── Columna principal ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 px-4 py-1.5 text-[11px] border-b border-slate-200 bg-white/70">
          <Home size={12} className="text-emerald-500" />
          <span className="font-bold text-slate-700">Centro de Diseño</span>
          <span className="text-slate-300">/</span>
          <span className="text-slate-500">Inicio · {p.nombre}</span>
          <span className="ml-auto flex items-center gap-1 text-[10px] font-semibold">
            {saving ? <span className="flex items-center gap-1 text-slate-400"><Loader2 size={11} className="animate-spin" /> Guardando…</span>
              : saved ? <span className="flex items-center gap-1 text-emerald-600"><Check size={11} /> Guardado</span>
              : <span className="flex items-center gap-1 text-slate-400"><Save size={11} /> Autoguardado activo</span>}
          </span>
        </div>

        {/* Toolbar VM */}
        <header className="flex items-center gap-3 px-4 py-2 bg-white border-b border-slate-200 shrink-0" data-testid="vm-toolbar">
          <div className="min-w-0 flex items-center gap-2">
            <div className="min-w-0">
              <div className="text-[9px] font-bold uppercase tracking-wide text-slate-400 leading-none">Proyecto actual</div>
              <div className="text-sm font-extrabold text-slate-800 truncate leading-tight">{p.nombre}</div>
            </div>
            <span className="ml-1 rounded-md px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: PETROL }}>
              {p.niveles.pisos} PISOS{p.niveles.incluirParapeto ? " + PARAPETO" : ""}
            </span>
            <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">{pres ? "CALCULADO" : "DRAFT"}</span>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            {isAdmin && <InicioCorexWidget />}
            <button onClick={restaurarOficial} disabled={restaurando} data-testid="vm-inicio-restaurar-oficial"
              className="flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[12px] font-bold text-sky-700 hover:bg-sky-100 transition-colors disabled:opacity-60">
              {restaurando ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Nuevo/Restaurar
            </button>
            <ToolbarButton testid="vm-tb-guardar" icon={Save} label="Guardar" onClick={guardarAhora} />
            <ToolbarButton testid="vm-tb-exportar-doc" icon={FileDown} label="Exportar Doc" items={docItems} variant="primary" />
            <ToolbarButton testid="vm-tb-exportar-planos" icon={Map} label="Exportar Planos" items={planoItems} />
            <button onClick={calcular} disabled={calc} data-testid="vm-calcular-btn"
              className="flex items-center gap-2 rounded-lg px-4 py-2 text-[12px] font-extrabold text-white shadow-lg transition-all hover:-translate-y-0.5 disabled:opacity-60"
              style={{ background: `linear-gradient(135deg, ${NEON}, #16a34a)` }}>
              {calc ? <Loader2 size={15} className="animate-spin" /> : <Calculator size={15} />} CALCULAR PROYECTO
            </button>
            <button data-testid="vm-tb-ayuda" onClick={() => flash("Configura terreno, niveles y ambientes; luego pulsa Calcular Proyecto.")}
              className="grid place-items-center h-8 w-8 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors">
              <HelpCircle size={15} />
            </button>
          </div>
        </header>

        {err && <div className="mx-4 mt-2 rounded-lg bg-red-50 border border-red-200 px-3 py-1.5 text-xs text-red-700" data-testid="vm-error">{err}</div>}

        {/* Grid de 6 bloques (denso) */}
        <main className="flex-1 overflow-y-auto p-3">
          <div className="grid grid-cols-1 xl:grid-cols-[13fr_7fr] gap-2 items-stretch">
            {/* ══ COLUMNA IZQUIERDA · Parámetros (65%) ══ */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-2 auto-rows-min min-w-0">
            {/* 1 · Dimensiones del terreno */}
            <Block n={1} title="Dimensiones del Terreno" icon={Ruler} variant="blue" className="xl:col-span-4 xl:order-1">
              <div className="grid grid-cols-2 gap-2">
                <NumField label="Ancho (frente)" value={p.terreno.ancho} onChange={(v) => set("terreno.ancho", v)} unit="m" testid="vm-ancho" />
                <NumField label="Largo (fondo)" value={p.terreno.largo} onChange={(v) => set("terreno.largo", v)} unit="m" testid="vm-largo" />
              </div>
              <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                <NumField label="Altura 1° Piso" value={p.terreno.altura1} onChange={(v) => set("terreno.altura1", v)} unit="m" testid="vm-altura1" />
                <NumField label="Altura típica (2°-5°)" value={p.terreno.alturaTipica} onChange={(v) => set("terreno.alturaTipica", v)} unit="m" testid="vm-altura-tipica" />
                <NumField label="Altura parapeto" value={p.terreno.alturaParapeto} onChange={(v) => set("terreno.alturaParapeto", v)} unit="m" testid="vm-altura-parapeto" />
              </div>
              <div className="mt-1.5 flex items-center gap-2 rounded-lg border px-2.5 py-1.5" style={{ borderColor: NEON, background: NEON_SOFT }}>
                <div className="min-w-0">
                  <div className="text-[8px] font-bold uppercase tracking-wider text-slate-500 leading-none">Área del terreno</div>
                  <div className="text-lg font-extrabold text-emerald-700 leading-tight" data-testid="vm-area">{n2(geo.area)} m²</div>
                </div>
                <div className="ml-auto text-right text-[10px] text-slate-500 leading-tight">{n2(p.terreno.ancho)}×{n2(p.terreno.largo)} m<br/>Perímetro <span data-testid="vm-perimetro">{n2(geo.perimetro)} m</span></div>
              </div>
            </Block>

            {/* 2 · Configuración de construcción */}
            <Block n={2} title="Configuración de Construcción" icon={Layers} variant="blue" className="xl:col-span-4 xl:order-2">
              <div className="flex gap-1 mb-1.5">
                {[["ACUMULADA", "Construcción acumulada"], ["ESPECIFICO", "Un nivel específico"]].map(([k, l]) => {
                  const locked = k === "ESPECIFICO" && !features.nivel_especifico;
                  return (
                    <button key={k} data-testid={`vm-modo-${k}`}
                      onClick={() => locked ? upsell("El trabajo por nivel específico está disponible en Pro y Premium.") : set("niveles.modo", k)}
                      className="relative flex-1 rounded-lg px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-colors"
                      style={p.niveles.modo === k ? { background: PETROL, color: "#fff" } : { background: "#f1f5f9", color: locked ? "#94a3b8" : "#64748b" }}>
                      {locked && <Lock size={9} className="inline mr-1 -mt-0.5" />}{l}
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-5 gap-1.5" data-testid="vm-niveles">
                {[1, 2, 3, 4, 5].map((lvl) => {
                  const isN = Number(p.niveles.pisos) === lvl;
                  const on = p.niveles.modo === "ESPECIFICO" ? isN : lvl <= Number(p.niveles.pisos);
                  const locked = lvl > 1 && !features.multinivel;
                  return (
                    <button key={lvl} data-testid={`vm-nivel-${lvl}`}
                      onClick={() => locked ? upsell("La construcción acumulada 1–5 pisos está disponible en Pro y Premium.") : set("niveles.pisos", lvl)}
                      className="relative rounded-lg border px-1 py-1.5 text-[10px] font-extrabold transition-all"
                      style={on ? { borderColor: NEON, background: NEON_SOFT, color: "#15803d" } : { borderColor: "#e2e8f0", background: "#f8fafc", color: locked ? "#cbd5e1" : "#64748b" }}>
                      <div className="text-sm">{lvl}°</div>
                      <div className="text-[8px] font-semibold leading-none mt-0.5 truncate">PISO</div>
                      {isN && <span className="absolute -top-1.5 -right-1.5 grid place-items-center h-4 w-4 rounded-full text-white" style={{ background: NEON }}><Check size={10} /></span>}
                      {locked && <span className="absolute -top-1.5 -right-1.5 grid place-items-center h-4 w-4 rounded-full bg-slate-400 text-white"><Lock size={9} /></span>}
                    </button>
                  );
                })}
              </div>
              {/* Línea de control: Parapeto (60%) + Factores de complejidad ultra-compactos (40%) */}
              <div className="mt-1.5 flex items-stretch gap-1.5">
                <button data-testid="vm-parapeto-toggle" onClick={() => set("niveles.incluirParapeto", !p.niveles.incluirParapeto)}
                  className="flex-[3] flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 hover:bg-slate-100 transition-colors">
                  <span className="text-[11px] font-semibold text-slate-700">Incluir parapeto</span>
                  <span className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0" style={{ background: p.niveles.incluirParapeto ? NEON : "#cbd5e1" }}>
                    <span className="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform" style={{ transform: p.niveles.incluirParapeto ? "translateX(18px)" : "translateX(2px)" }} />
                  </span>
                </button>
                <div className="flex-[2] flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-1.5 py-1" data-testid="vm-factores-block" title="Factores de complejidad por altura (%) — sobrecosto de acarreo/rendimiento por nivel">
                  <TrendingUp size={12} className="text-amber-500 shrink-0" />
                  {[["p3", "3°"], ["p4", "4°"], ["p5", "5°"]].map(([k, lbl]) => (
                    <div key={k} className="flex flex-col items-center flex-1 min-w-0">
                      <span className="text-[7px] font-bold uppercase text-slate-400 leading-none mb-0.5">{lbl}</span>
                      <div className="flex items-center w-full rounded border border-slate-200 focus-within:border-amber-400 px-1">
                        <input data-testid={`vm-factor-${k}`} type="number" min="0" step="0.5" value={p.niveles.factores?.[k] ?? ""}
                          onChange={(e) => set(`niveles.factores.${k}`, e.target.value === "" ? 0 : parseFloat(e.target.value))}
                          className="w-full text-[10px] font-bold text-slate-700 text-right py-0.5 focus:outline-none bg-transparent" />
                        <span className="text-[8px] font-bold text-slate-400">%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Configuración activa (compacta, una línea) */}
              <div className="mt-1.5 flex items-center gap-2 rounded-lg border px-2.5 py-1.5" style={{ borderColor: NEON, background: NEON_SOFT }}>
                <Check size={13} className="text-emerald-600 shrink-0" />
                <div className="min-w-0 leading-tight">
                  <span className="text-[11px] font-extrabold text-slate-800">Pisos {floors.join("+")}{p.niveles.incluirParapeto ? " + Parapeto" : ""}</span>
                  <span className="text-[10px] text-slate-500"> · Altura total <span data-testid="vm-altura">{n2(geo.alturaTotal)} m</span></span>
                </div>
              </div>
              {/* Firma profesional (compacta) */}
              <div className="mt-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5" data-testid="vm-firma-block">
                <div className="flex items-center gap-1 text-[8px] font-bold uppercase tracking-wide text-slate-400 mb-1"><ClipboardCheck size={10} /> Firma profesional (PDF)</div>
                <div className="grid grid-cols-[1fr_auto] gap-1.5">
                  <input data-testid="vm-firma-ingeniero" value={p.firma?.ingeniero || ""} onChange={(e) => set("firma.ingeniero", e.target.value)}
                    placeholder="Ingeniero responsable" className="w-full rounded-md border border-slate-200 px-2 py-1 text-[11px] focus:border-emerald-400 focus:outline-none" />
                  <input data-testid="vm-firma-cip" value={p.firma?.cip || ""} onChange={(e) => set("firma.cip", e.target.value)}
                    placeholder="N° CIP" className="w-24 rounded-md border border-slate-200 px-2 py-1 text-[11px] focus:border-emerald-400 focus:outline-none" />
                </div>
              </div>
            </Block>

            {/* 4 · Programa arquitectónico */}
            <Block n={4} title="Programa Arquitectónico y Control de Áreas" icon={Table2} variant="blue" className="xl:col-span-7 xl:order-4"
              right={
                <div className="flex items-center gap-1.5">
                  <ToolbarButton testid="vm-plantilla-rapida" icon={LayoutTemplate} label="Plantilla rápida" items={Object.entries(TEMPLATES).map(([k, t]) => ({ testid: `vm-tpl-${k}`, label: t.label, icon: LayoutTemplate, onClick: () => applyTemplate(k) }))} />
                  <button onClick={addAmbiente} data-testid="vm-add-ambiente" className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-bold text-white shadow-sm" style={{ background: NEON }}><Plus size={13} /> Agregar Ambiente</button>
                </div>
              }>
              <div className="overflow-x-auto">
                <table className="w-full text-[12px] border-collapse" data-testid="vm-ambientes-table">
                  <thead>
                    <tr className="text-left text-[9px] uppercase tracking-wider text-white bg-[#1D4ED8] rounded-t-md" data-testid="vm-amb-thead">
                      <th className="py-0.5 px-1 font-semibold w-8 rounded-tl-md"></th>
                      <th className="py-0.5 px-1.5 font-semibold min-w-[110px]">Ambiente</th>
                      <th className="py-0.5 px-1.5 font-semibold w-28">Tipo de piso</th>
                      <th className="py-0.5 px-1.5 font-semibold text-right w-16 whitespace-nowrap">Á. mín.<br/><span className="text-[7px] font-normal opacity-80">NTP</span></th>
                      <th className="py-0.5 px-1.5 font-semibold text-right w-20 whitespace-nowrap">Á. propuesta<br/><span className="text-[7px] font-normal opacity-80">m²</span></th>
                      <th className="py-0.5 px-1.5 font-semibold text-center w-16">Cant.</th>
                      <th className="py-0.5 px-1.5 font-semibold text-right w-20 whitespace-nowrap">Á. total<br/><span className="text-[7px] font-normal opacity-80">m²</span></th>
                      <th className="py-0.5 px-1.5 font-semibold text-center w-14 rounded-tr-md">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.ambientes.length === 0 && (
                      <tr><td colSpan={8} className="py-5 text-center text-slate-400 text-xs">Sin ambientes. Agrega el primero con “Agregar Ambiente” o usa una “Plantilla rápida”.</td></tr>
                    )}
                    {p.ambientes.map((amb) => {
                      const areaTerreno = (Number(p.terreno.ancho) || 0) * (Number(p.terreno.largo) || 0);
                      const isAL = isAreaLibre(amb);
                      const areaTotal = ambArea(amb, areaTerreno);
                      const Icon = ambIcon(amb.nombre);
                      return (
                        <tr key={amb.id} onClick={() => setSelRow(amb.id)} className={`border-b border-slate-100 cursor-pointer transition-colors ${selRow === amb.id ? "bg-cyan-50" : "hover:bg-slate-50/70"}`} data-testid={`vm-amb-row-${amb.id}`}>
                          <td className="px-1 py-0.5"><span className="grid place-items-center h-5 w-5 rounded-md bg-slate-100 text-slate-500"><Icon size={13} /></span></td>
                          <td className="px-1.5 py-0.5"><input value={amb.nombre} onChange={(e) => setAmb(amb.id, "nombre", e.target.value)} className="w-full min-w-0 bg-transparent font-semibold text-slate-700 outline-none focus:bg-white rounded px-1" /></td>
                          <td className="px-1.5 py-0.5">
                            {isAL ? (
                              <select value={(amb.tipoPiso && amb.tipoPiso !== "-") ? amb.tipoPiso : "Pulido"} onChange={(e) => setAmb(amb.id, "tipoPiso", e.target.value)} data-testid={`vm-amb-tipo-${amb.id}`} title="Acabado del patio: suma al costo de obra (Cap. 03), no al área techada" className="w-full bg-slate-50 rounded px-1.5 py-0.5 font-semibold text-slate-700 outline-none border border-transparent focus:border-slate-300 text-[11px]">
                                {TIPOS_PISO.filter((t) => t !== "-").map((t) => <option key={t} value={t}>{t}</option>)}
                              </select>
                            ) : (
                              <select value={amb.tipoPiso} onChange={(e) => setAmb(amb.id, "tipoPiso", e.target.value)} data-testid={`vm-amb-tipo-${amb.id}`} className="w-full bg-slate-50 rounded px-1.5 py-0.5 font-semibold text-slate-700 outline-none border border-transparent focus:border-slate-300 text-[11px]">
                                {TIPOS_PISO.map((t) => <option key={t} value={t}>{t}</option>)}
                              </select>
                            )}
                          </td>
                          <td className="px-1.5 py-0.5 text-right text-[11px] text-slate-400">{ambMinDisplay(amb)}</td>
                          <td className="px-1.5 py-0.5 text-right">
                            <div className="inline-flex items-center rounded bg-slate-50 border border-transparent focus-within:border-slate-300 focus-within:bg-white">
                              <SmartAmbInput id={`vm-area-input-${amb.id}`} value={amb.areaProp} onCommit={(v) => setAmb(amb.id, "areaProp", v)} testid={`vm-amb-area-${amb.id}`} title={isAL ? "Porcentaje (%) del terreno" : undefined} className="w-16 bg-transparent px-1.5 py-0.5 text-right font-semibold text-slate-800 outline-none" />
                              {isAL && <span className="pr-1.5 text-[10px] font-bold text-slate-400">%</span>}
                            </div>
                          </td>
                          <td className="px-1.5 py-0.5 text-center"><SmartAmbInput value={amb.cantidad} disabled={isAL} onCommit={(v) => setAmb(amb.id, "cantidad", v)} testid={`vm-amb-cant-${amb.id}`} className={`w-14 rounded px-1.5 py-0.5 text-center font-semibold outline-none border border-transparent focus:border-slate-300 focus:bg-white ${isAL ? "bg-slate-100 text-slate-300 cursor-not-allowed" : "bg-slate-50"}`} /></td>
                          <td className="px-1.5 py-0.5 text-right font-extrabold text-slate-800" data-testid={`vm-amb-total-${amb.id}`}>{n2(areaTotal)}</td>
                          <td className="px-1.5 py-0.5">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => focusArea(amb.id)} data-testid={`vm-amb-edit-${amb.id}`} title="Editar" className="grid place-items-center h-6 w-6 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"><Pencil size={13} /></button>
                              <button onClick={() => rmAmbiente(amb.id)} data-testid={`vm-amb-rm-${amb.id}`} title="Eliminar" className="grid place-items-center h-6 w-6 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: PETROL }}>
                      <td colSpan={6} className="px-3 py-2 text-left text-[11px] font-extrabold uppercase tracking-wide text-white">Total Área Techada</td>
                      <td className="px-1.5 py-2 text-right text-base font-extrabold text-white" data-testid="vm-total-techada">{n2(totalTabla)}</td>
                      <td className="px-1.5 py-2 text-left text-[11px] font-bold text-white/70">m²</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {(() => {
                const at = (Number(p.terreno.ancho) || 0) * (Number(p.terreno.largo) || 0);
                const patio = p.ambientes.filter((a) => isAreaLibre(a)).reduce((s, a) => s + ambArea(a, at), 0);
                return patio > 0 ? (
                  <div className="mt-1 px-3 text-[10px] text-slate-500" data-testid="vm-area-libre-ref">
                    Área Libre (Patio): <span className="font-semibold text-slate-600">{n2(patio)} m²</span> <span className="text-slate-400">(no se suma al área techada)</span>
                  </div>
                ) : null;
              })()}
            </Block>

            {/* 5 · Control de área con gauge (Fila 1 · dona protagonista izquierda, textos compactos derecha) */}
            <Block n={5} title={`Control de Área · Piso ${pisoSel}`} icon={Gauge} variant="blue" className="xl:col-span-4 xl:order-3"
              right={
                <div className="flex gap-1" data-testid="vm-control-pisos">
                  {floors.map((nn) => (
                    <button key={nn} data-testid={`vm-control-piso-${nn}`} onClick={() => setPisoSel(nn)}
                      className="rounded-md px-2 py-0.5 text-[10px] font-bold transition-colors"
                      style={pisoSel === nn ? { background: PETROL, color: "#fff" } : { background: "#f1f5f9", color: "#64748b" }}>Piso {nn}</button>
                  ))}
                </div>
              }>
              <div className="flex items-center gap-3">
                <AreaGauge value={control.util} color={control.color} />
                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="rounded-lg bg-slate-50 px-2.5 py-1.5">
                    <div className="text-[8px] font-bold uppercase tracking-wider text-slate-400 leading-none">Área programada</div>
                    <div className="text-[15px] font-extrabold text-slate-800 leading-tight"><span data-testid="vm-area-programada">{n2(control.programada)}</span> <span className="text-[10px] font-bold text-slate-400">m²</span></div>
                  </div>
                  <div className="rounded-lg bg-slate-50 px-2.5 py-1.5">
                    <div className="text-[8px] font-bold uppercase tracking-wider text-slate-400 leading-none">Área disponible</div>
                    <div className="text-[15px] font-extrabold text-slate-800 leading-tight"><span data-testid="vm-area-disponible">{n2(control.disponible)}</span> <span className="text-[10px] font-bold text-slate-400">m²</span></div>
                  </div>
                  <div className="flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[10px] font-extrabold leading-tight"
                    style={{ background: control.estado === "OK" ? "rgba(34,197,94,0.14)" : control.estado === "EXCEDE" ? "rgba(220,38,38,0.10)" : "rgba(249,115,22,0.12)", color: control.color }}
                    data-testid="vm-badge-compat">
                    {control.estado === "OK" && <Check size={12} className="shrink-0" />} <span className="truncate">{control.mensaje}</span>
                  </div>
                </div>
              </div>
              <div className="mt-2 text-[9px] text-slate-500 leading-tight" data-testid="vm-desglose-ocupacion">
                Techada <span className="font-semibold text-slate-700">{n2(control.techada)}</span> + Patio <span className="font-semibold text-slate-700">{n2(control.patio)}</span> = <span className="font-bold text-slate-800">{n2(control.programada)} m²</span>
              </div>
            </Block>

            {/* 6 · Resumen de presupuesto · Espejo del Oráculo Sandoval (Costo Directo) — Fila 3 (cierre, compacto izquierda) */}
            <Block n={6} title="Resumen de Presupuesto" icon={Calculator} variant="blue" className="xl:col-span-5 xl:order-5">
              <SandovalResumenCard />
              <RentabilidadCard margen={p.margenUtilidad} incremento={p.incrementoPisoVenta} onSet={set} />
            </Block>
            {/* Bloque comercial compacto (F5): alineado bajo Programa Arquitectónico (mismo ancho, col-span-7) */}
            <div className="min-w-0 xl:col-span-7 xl:order-6">
              <ProgramaComercial plan={planActual} licencia={miLicencia} isAdmin={isAdmin} />
            </div>
            </div>
            {/* ══ COLUMNA DERECHA · Lienzo Maestro (35%, altura completa) ══ */}
            <div className="min-w-0 flex">
              <Block n={3} title="Diseño y Distribución" icon={Building2} variant="blue" className="w-full xl:h-full">
                <VmDesignStage project={p} onSet={set} geo={geo}
                  ambientes={(p.ambientes || []).map((a) => ({ id: a.id, nombre: a.nombre, m2: ambArea(a, (Number(p?.terreno?.ancho) || 0) * (Number(p?.terreno?.largo) || 0)), libre: isAreaLibre(a), cantidad: Math.max(0, Math.round(Number(a.cantidad) || 0)) }))}
                  onAmbienteArea={(id, m2) => { const a = p.ambientes.find((x) => x.id === id); if (!a || isAreaLibre(a)) return; const c = Math.max(1, Number(a.cantidad) || 1); setAmb(id, "areaProp", Math.max(0.5, Math.round((m2 / c) * 100) / 100)); }} />
              </Block>
            </div>
          </div>
        </main>

        {/* Barra inferior */}
        <div className="flex items-center gap-3 border-t border-slate-200 bg-white px-4 py-2 shrink-0">
          <button onClick={limpiarTodo} data-testid="vm-limpiar" className="rounded-lg border border-slate-200 px-3 py-2 text-[12px] font-bold text-slate-500 hover:bg-slate-50">Limpiar Todo</button>
          <span className="text-[11px] text-slate-400 hidden sm:flex items-center gap-1.5">
            {control.compat ? <><Check size={13} className="text-emerald-500" /> Todos los datos están listos. Puede calcular el proyecto.</> : "Ajusta la configuración para continuar"}
          </span>
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 backdrop-blur-sm p-6" onClick={() => setLightbox(null)} data-testid="vm-lightbox">
          <div className="absolute top-4 left-5 text-white text-sm font-bold">{lightbox.title}</div>
          <button onClick={() => setLightbox(null)} data-testid="vm-lightbox-close"
            className="absolute top-4 right-5 grid place-items-center h-10 w-10 rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors">
            <X size={20} />
          </button>
          <img src={lightbox.src} alt={lightbox.title} className="max-h-[88vh] max-w-[92vw] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {toast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] rounded-full px-4 py-2 text-[12px] font-semibold text-white shadow-lifted" style={{ background: PETROL }} data-testid="vm-toast">
          {toast}
        </div>
      )}
    </div>
  );
}
